import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  Cooperativa,
  CooperativaPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCooperativa = ({
  params,
  enabled = false
}: {
  params: GetCooperativaParams,
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ['cooperativas', ...Object.values(params || {})],
    enabled: enabled,
    queryFn: async () => {
      return getCooperativas(params);
    },
  });
};

export const useGetCooperativa = (id: string) => {
  return useQuery({
    queryKey: ['cooperativas', id],
    queryFn: async () => {
      return getCooperativa(id);
    },
  });
};

export const useCreateCooperativa = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCooperativa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cooperativas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Entidad financiera creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCooperativa = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  onSuccess
}: UseMutationsParams & { onSuccess?: () => void }) => { // Update this line
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCooperativa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['type-regimen'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Tipo regimen actualizado correctamente',
          AlertSeverityEnum.success
        );

      onSuccess && onSuccess(); // Add this line
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export interface GetCooperativaParams extends Partial<Cooperativa> {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateCooperativaParams = Omit<
  Cooperativa,
  'id_entidad_financiera'
>;
export type UpdateCooperativaParams = {
  id: string;
  data: CreateCooperativaParams;
};

export const getCooperativas = async (
  params?: GetCooperativaParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<CooperativaPagingRes>(
    `${urlBackend}api/v1/cooperativa/?${queryParams}`,
    true
  );
};

export const getCooperativa = async (id: string) => {
  try {
    return await get<Cooperativa>(`${urlBackend}api/v1/cooperativa/${id}/`, true);
  }
  catch (error) {
    handleAxiosError(error);
  }
};

export const createCooperativa = async (
  data: CreateCooperativaParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<Cooperativa>(`${urlBackend}api/v1/cooperativa/`, data, true);

};

export const updateCooperativa = async ({
  id,
  data,
}: UpdateCooperativaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<Cooperativa>(`${urlBackend}api/v1/cooperativa/${id}/`, data, true);
};
